import { useNavigate, useParams } from 'react-router-dom';
import { AccountBalance, OpenInNew } from '@mui/icons-material';
import { Button, Card, CardContent, CircularProgress, Typography } from '@mui/material';

import { BackButton } from '../components/back-button';
import { CardTitle } from '../components/card-title';
import { useBillingDashboardOrgDetailQuery } from '../store/hooks';
import {
  formatCents,
  formatPercent,
  formatRelativeTime,
  getStalenessColor,
} from '../utils/formatting';

import AdminClaimWorklist from './admin-claim-worklist';

function MetricTile({
  label,
  value,
  className,
}: {
  label: string;
  value: string | number;
  className?: string;
}) {
  return (
    <div className="p-3">
      <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">{label}</div>
      <div className={`text-xl font-bold ${className || 'text-gray-900'}`}>{String(value)}</div>
    </div>
  );
}

export default function BillingDashboardDetail() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();

  const { currentData: data, isFetching, error } = useBillingDashboardOrgDetailQuery(
    { organizationId: organizationId ?? '' },
    {
      skip: !organizationId,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      refetchOnMountOrArgChange: 15,
    },
  );

  if (isFetching && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <CircularProgress size={32} />
      </div>
    );
  }

  if (!organizationId || error || !data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <main className="max-w-7xl mx-auto px-4 py-8">
          <BackButton fallbackUrl="/admin-v2/billing-dashboard" />
          <div className="text-center mt-12">
            <Typography variant="h5" className="text-gray-900 font-bold">
              {error ? 'Error loading billing data' : 'No billing data found'}
            </Typography>
            <Typography className="text-gray-600 mt-2">
              This organization may not have any billing activity yet.
            </Typography>
            <Button onClick={() => navigate('/admin-v2/billing-dashboard')} className="mt-4">
              Back to Dashboard
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-8">
        <BackButton fallbackUrl="/admin-v2/billing-dashboard" />

        {/* Header */}
        <div className="flex items-center justify-between mt-4 mb-6">
          <div className="flex items-center gap-3">
            <AccountBalance className="text-blue-600" fontSize="large" />
            <div>
              <Typography variant="h4" className="font-bold text-gray-900">
                {data.organization_name || 'Unnamed Organization'}
              </Typography>
              <Typography variant="body2" className="text-gray-500">
                Billing Overview
              </Typography>
            </div>
          </div>
          <Button
            variant="outlined"
            size="small"
            endIcon={<OpenInNew />}
            onClick={() => navigate(`/admin-v2/organization/${organizationId}`)}
          >
            Full Org Details
          </Button>
        </div>

        <AdminClaimWorklist key={organizationId} organizationId={organizationId} />

        {/* Section 2: Financials */}
        <Card className="mb-4">
          <CardContent>
            <CardTitle className="mb-3">Historical financials</CardTitle>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 border border-gray-100 rounded-lg divide-x divide-gray-100">
              <MetricTile label="Total Charges" value={formatCents(data.total_charge_cents)} />
              <MetricTile label="Total Paid" value={formatCents(data.total_paid_cents)} />
              <MetricTile label="Balance" value={formatCents(data.total_balance_cents)} />
              <MetricTile
                label="Collection Rate"
                value={formatPercent(data.collection_rate_pct)}
                className={
                  data.collection_rate_pct != null && data.collection_rate_pct >= 80
                    ? 'text-green-600'
                    : 'text-gray-900'
                }
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 border border-gray-100 rounded-lg divide-x divide-gray-100 mt-2">
              <MetricTile
                label="Denial Rate"
                value={formatPercent(data.denial_rate_pct)}
                className={
                  data.denial_rate_pct != null && data.denial_rate_pct > 10
                    ? 'text-red-600'
                    : 'text-gray-900'
                }
              />
              <MetricTile label="Historical denials" value={data.denial_count ?? 0} />
              <MetricTile label="Historical rejections" value={data.rejection_count ?? 0} />
              <MetricTile label="Historical reversals" value={data.reversal_count ?? 0} />
            </div>
          </CardContent>
        </Card>

        {/* Section 3: Staleness & Activity */}
        <Card className="mb-4">
          <CardContent>
            <CardTitle className="mb-3">Activity & Staleness</CardTitle>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 border border-gray-100 rounded-lg divide-x divide-gray-100">
              <MetricTile
                label="Oldest Draft Age"
                value={
                  data.oldest_draft_age_days != null
                    ? `${String(data.oldest_draft_age_days)} days`
                    : '-'
                }
                className={getStalenessColor(data.oldest_draft_age_days)}
              />
              <MetricTile
                label="Oldest Ready Age"
                value={
                  data.oldest_ready_age_days != null
                    ? `${String(data.oldest_ready_age_days)} days`
                    : '-'
                }
                className={getStalenessColor(data.oldest_ready_age_days)}
              />
              <MetricTile
                label="Last Claim Created"
                value={formatRelativeTime(data.last_claim_created_at)}
              />
              <MetricTile
                label="Last Submission"
                value={formatRelativeTime(data.last_submission_at)}
              />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 border border-gray-100 rounded-lg divide-x divide-gray-100 mt-2">
              <MetricTile
                label="Active Billing Providers"
                value={data.active_billing_providers ?? 0}
              />
              <MetricTile label="Claims Created (30d)" value={data.claims_created_last_30d ?? 0} />
              <MetricTile
                label="Claims Submitted (30d)"
                value={data.claims_submitted_last_30d ?? 0}
              />
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Remittances */}
        <Card className="mb-4">
          <CardContent>
            <CardTitle className="mb-3">Remittances</CardTitle>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 border border-gray-100 rounded-lg divide-x divide-gray-100">
              <MetricTile label="Remittances Received" value={data.remittances_received ?? 0} />
              <MetricTile
                label="Total Payments"
                value={formatCents(data.total_remittance_payment_cents)}
              />
              <MetricTile
                label="Last Remittance"
                value={formatRelativeTime(data.last_remittance_at)}
              />
              <MetricTile
                label="Unmatched Claims"
                value={data.unmatched_remittance_claims ?? 0}
                className={
                  (data.unmatched_remittance_claims ?? 0) > 0
                    ? 'text-yellow-600 font-medium'
                    : 'text-gray-900'
                }
              />
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
